import { isStoryLoading } from '@src/components/Dashboard/utils';
import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import {
  type AnonymousSpeaker,
  resolveTranscriptSpeakers,
} from '@src/services/SpeakerManagementService';
import {
  useDashboard,
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { SpeakerIdentificationState } from '@src/services/speakers/SpeakerIdentificationService';
import type { ToastRequest } from '@src/stores/toast';

const logger = LogManager.getLogger('hook:useSpeakerIdentificationPrompt');

/** The pending toast shown until the modal opens. */
export const speakerPromptPendingMessage = (title: string) =>
  `${title} · Identifying speakers…`;

const pendingToast = (title: string): ToastRequest => ({
  state: 'pending',
  message: speakerPromptPendingMessage(title),
});

export interface SpeakerIdentificationPrompt {
  anonymousSpeakers: AnonymousSpeaker[];
  mappings: Record<string, string>;
  /** The background run's result; absent, the modal runs the analysis itself. */
  precomputed?: Pick<
    SpeakerIdentificationState,
    'identified' | 'frameAnalysisResults'
  >;
}

type Candidate = Omit<SpeakerIdentificationPrompt, 'precomputed'>;

/**
 * Offers speaker identification once for a story this session uploaded, while
 * it is open, when at least one of its speakers is not yet mapped to a Dato
 * speaker. `speakerPromptState` makes it once per story (across Content Studio
 * and Creator Studio alike): `queued` by the background run, `waiting` once
 * evaluated here, `done` once shown or skipped.
 *
 * The analysis runs during the upload (useSpeakerIdentificationPrefetch) and
 * the modal opens straight onto its result, as soon as there is one — during
 * Versioning when the transcript named everyone, after the frame passes
 * (upload ready) otherwise. When the upload finishes while this is still
 * waiting, the progress toast UploadCompletionToaster hands off becomes
 * "Identifying speakers…"; `onReady` takes it down as the modal appears, and
 * when there is nothing to identify it becomes the "is ready" toast it
 * replaced. Outside an upload (opened later, or no background result) it
 * shows its own pending toast only when there is a wait.
 *
 * Call from an `observer` component: it reads MobX state during render.
 */
export function useSpeakerIdentificationPrompt(): {
  prompt: SpeakerIdentificationPrompt | null;
  onReady: () => void;
  close: () => void;
} {
  const videoCreator = useVideoCreatorStore();
  const uploadFlow = useUserStoryUploadFlow();
  const dashboardStore = useDashboard();
  const userIdentity = useUserIdentity();
  const speakerService = useSpeakerManagementService();
  const { enableSpeakersIdentification, enableTranslationReviewerMode } =
    useFlagsCombination(userIdentity.currentRole);
  // Decided to prompt, waiting on the background run to finish.
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [prompt, setPrompt] = useState<SpeakerIdentificationPrompt | null>(
    null,
  );
  const pendingToastIdRef = useRef<number | null>(null);
  // The story last evaluated. Guards the one-shot evaluation against the
  // effect re-running before its fetch resolves (StrictMode's double effect,
  // or a dep changing): a second pass would find the handed-over toast already
  // taken and stack a second "Identifying speakers…" toast.
  const evaluatedStoryIdRef = useRef<string | null>(null);
  // Bumped per evaluation. Leaving clears the guard above, so coming back
  // starts a new evaluation while the old fetch may still be out; only the
  // newest may act, or both would show a pending toast and orphan one.
  const evaluationTokenRef = useRef(0);

  const storyId = videoCreator.story?.id;
  const currentStoryIdRef = useRef(storyId);
  currentStoryIdRef.current = storyId;
  const promptState = storyId
    ? uploadFlow.speakerPromptState[storyId]
    : undefined;
  // `waiting` too: a prompt left waiting when the user moved away is picked up
  // again when they come back.
  const isQueued =
    !!storyId &&
    enableSpeakersIdentification &&
    !enableTranslationReviewerMode &&
    (promptState === 'queued' || promptState === 'waiting');
  // Still in the pipeline: its own progress toast is up, so no second toast.
  const dashboardStory = storyId
    ? dashboardStore.allStories.find(s => s.id === storyId)
    : undefined;
  const isProcessing = !!dashboardStory && isStoryLoading(dashboardStory);
  const heldToast = storyId
    ? uploadFlow.speakerPromptToasts[storyId]
    : undefined;
  const prefetch = storyId
    ? uploadFlow.speakerPrefetchRecord[storyId]
    : undefined;
  const prefetchStatus = prefetch?.status;
  const prefetchRunning =
    prefetchStatus === 'transcript' ||
    prefetchStatus === 'awaiting-frames' ||
    prefetchStatus === 'frames';
  // Its own "Identifying speakers…" toast only when there will be a wait —
  // the background run is unfinished, or absent so the modal analyzes — and no
  // progress toast is already up. Read when the evaluation resolves.
  const needsOwnToastRef = useRef(false);
  needsOwnToastRef.current =
    !isProcessing && (prefetchRunning || prefetchStatus !== 'done');

  // Story-scoped transcript, the same one the identification modal reads.
  const transcriptionStore = videoCreator.rootStore?.transcriptionStore;
  const originalElements =
    transcriptionStore?.getOriginalTranscription()?.elements;
  // The store is shared across stories: only trust a loaded transcript that
  // belongs to this story, not the previous one still held during a switch.
  const transcriptReady =
    !!storyId &&
    !!transcriptionStore?.isTranscriptionReady &&
    transcriptionStore.loadedStoryId === storyId &&
    !!originalElements?.length;

  const endPendingToast = useCallback(() => {
    if (pendingToastIdRef.current !== null) {
      videoCreator.dismissToast(pendingToastIdRef.current);
      pendingToastIdRef.current = null;
    }
  }, [videoCreator]);

  /** Nothing to identify: stop, and put back the "is ready" toast if held. */
  const skip = useCallback(
    (id: string) => {
      uploadFlow.setSpeakerPromptState(id, 'done');
      const held = uploadFlow.takeSpeakerPromptToast(id);
      if (held) {
        videoCreator.updateToast(held.toastId, {
          state: 'success',
          message: `${held.title} is ready`,
          delay: 5000,
        });
      }
    },
    [uploadFlow, videoCreator],
  );

  // The transcript loads lazily (e.g. only on the Transcript tab), so ask for it.
  useEffect(() => {
    if (!isQueued || !storyId) return;
    videoCreator
      .loadTranscriptIfNeeded()
      .then((loaded: boolean) => {
        if (!loaded && currentStoryIdRef.current === storyId) skip(storyId);
      })
      .catch((error: unknown) => {
        logger.warn('Failed to load transcript', extractErrorInfo(error));
        if (currentStoryIdRef.current === storyId) skip(storyId);
      });
  }, [isQueued, storyId, videoCreator, skip]);

  useEffect(() => {
    if (!isQueued || !storyId || !transcriptReady || !originalElements) return;
    if (evaluatedStoryIdRef.current === storyId) return;
    evaluatedStoryIdRef.current = storyId;
    // Evaluated once per story per mount. The fetch below is not cancelled by
    // this effect re-running — only by the user leaving the story.
    uploadFlow.setSpeakerPromptState(storyId, 'waiting');
    const token = ++evaluationTokenRef.current;

    // Fresh mappings: speakers named by hand during the upload need no prompt.
    speakerService
      .fetchSpeakersForStory(storyId)
      .then(result => {
        if (currentStoryIdRef.current !== storyId) {
          const held = uploadFlow.takeSpeakerPromptToast(storyId);
          if (held) videoCreator.dismissToast(held.toastId);
          return;
        }
        // Back on the story, but a newer evaluation has taken over.
        if (token !== evaluationTokenRef.current) return;
        if (!result.ok) {
          logger.warn('Skipping speaker prompt: fetch failed', {
            storyId,
            error: result.error.message,
          });
          skip(storyId);
          return;
        }
        const { mappings } = result.value;
        const { speakerLabels, anonymous } = resolveTranscriptSpeakers({
          elementLists: [originalElements],
          mappings,
        });
        logger.info('Evaluated speaker identification prompt', {
          storyId,
          speakerCount: speakerLabels.length,
          unidentifiedCount: anonymous.length,
        });
        if (anonymous.length === 0) {
          skip(storyId);
          return;
        }
        const held = uploadFlow.takeSpeakerPromptToast(storyId);
        if (held) {
          pendingToastIdRef.current = videoCreator.updateToast(
            held.toastId,
            pendingToast(held.title),
          );
        } else if (needsOwnToastRef.current) {
          pendingToastIdRef.current = videoCreator.showToast(
            pendingToast(videoCreator.story?.title || 'Your video'),
          );
        }
        setCandidate({ anonymousSpeakers: anonymous, mappings });
      })
      .catch((error: unknown) => {
        logger.warn('Skipping speaker prompt: evaluation failed', {
          storyId,
          error: extractErrorInfo(error),
        });
        if (
          currentStoryIdRef.current === storyId &&
          token === evaluationTokenRef.current
        ) {
          skip(storyId);
        }
      });
  }, [
    isQueued,
    storyId,
    transcriptReady,
    originalElements,
    speakerService,
    uploadFlow,
    videoCreator,
    skip,
  ]);

  // The upload finished while this prompt was waiting on the frame passes:
  // take over the progress toast UploadCompletionToaster handed off.
  useEffect(() => {
    if (!candidate || !heldToast || !storyId) return;
    const held = uploadFlow.takeSpeakerPromptToast(storyId);
    if (!held) return;
    endPendingToast();
    pendingToastIdRef.current = videoCreator.updateToast(
      held.toastId,
      pendingToast(held.title),
    );
  }, [
    candidate,
    heldToast,
    storyId,
    uploadFlow,
    videoCreator,
    endPendingToast,
  ]);

  // Open once the background run has an answer: its result when done —
  // during Versioning when the transcript named everyone; otherwise after the
  // frame passes. Without one (never started, failed) the modal runs the
  // analysis itself.
  useEffect(() => {
    if (!candidate || !storyId || prefetchRunning) return;
    uploadFlow.setSpeakerPromptState(storyId, 'done');
    setPrompt({
      ...candidate,
      precomputed: prefetch?.status === 'done' ? prefetch.result : undefined,
    });
    setCandidate(null);
  }, [candidate, storyId, prefetchRunning, prefetch, uploadFlow]);

  // Leaving the story drops an open prompt, and its pending toast, with it.
  // A prompt left `waiting` is evaluated again when the user comes back.
  useEffect(() => {
    if (evaluatedStoryIdRef.current !== storyId) {
      evaluatedStoryIdRef.current = null;
    }
    setCandidate(null);
    setPrompt(null);
    return endPendingToast;
  }, [storyId, endPendingToast]);

  const close = useCallback(() => {
    endPendingToast();
    setPrompt(null);
  }, [endPendingToast]);

  return { prompt, onReady: endPendingToast, close };
}
